import { move } from "@prismicio/editor-support/Array"
import { findFocusableAncestor, isElement } from "@prismicio/editor-support/DOM"
import { useStableCallback } from "@prismicio/editor-support/React"
import {
  type DragEvent,
  type LiHTMLAttributes,
  memo,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"

import styles from "./AnimatedList.module.css"
import { type AutoScrollOptions, useAutoScroll } from "./autoScroll"
import { DragPreview, type DragPreviewRenderer } from "./DragPreview"
import { getMainElement, type State } from "./layout"

//--------------------------------------
//  useDndList
//--------------------------------------

interface UseDndListProps<ITEM> extends AutoScrollOptions {
  containerRef: RefObject<HTMLUListElement>
  items: ITEM[]
  itemStates: State<ITEM>[]
  direction: "horizontal" | "vertical"
  listId: string
  renderDragPreview?: RenderDragPreview<ITEM>
  onReorder?: (items: ITEM[]) => void
}

export function useDndList<ITEM>(props: UseDndListProps<ITEM>) {
  const {
    containerRef,
    items,
    itemStates,
    direction,
    renderDragPreview,
    onReorder,
    listId,
  } = props

  // No drag & drop support? Nothing to do.
  /* eslint-disable react-hooks/rules-of-hooks */
  if (!onReorder) return {}

  const previewRef = useRef<DragPreviewRenderer>(null)
  const willLeave = useRef(false)
  const [dragIndex, setDragIndex] = useState<number>()
  const [dropIndex, setDropIndex] = useState<number>()
  const [dragListId, setDragListId] = useState<string | null>(null)

  const preview = renderDragPreview ? (
    <DragPreview ref={previewRef} direction={direction}>
      {renderDragPreviewKey}
    </DragPreview>
  ) : null

  function renderDragPreviewKey(key: string) {
    const itemState = itemStates.find((item) => item.key === key)

    // Should never happen. AnimatedList keeps items and itemStates in sync.
    if (!itemState || !renderDragPreview) return null

    const { item, index } = itemState

    return renderDragPreview({ item, index })
  }

  const dragState: DragState = {
    previewRef,
    dragIndex,
    dropIndex,
    dragListId,
    onDragStart(index: number) {
      autoScroll.start()
      setDragIndex(index)
      setDragListId(listId)
    },
    onDragEnd() {
      autoScroll.stop()
      setDragIndex(undefined)
      setDropIndex(undefined)
      setDragListId(null)
    },
    onDragOver({ clientX, clientY, rect, itemIndex }) {
      willLeave.current = false

      if (direction === "vertical") {
        const halfHeight = rect.top + rect.height / 2
        const newDropIndex = clientY < halfHeight ? itemIndex : itemIndex + 1
        setDropIndex(newDropIndex)
      } else {
        const halfWidth = rect.left + rect.width / 2
        const newDropIndex = clientX < halfWidth ? itemIndex : itemIndex + 1
        setDropIndex(newDropIndex)
      }
    },
    onDragLeave() {
      willLeave.current = true

      setTimeout(() => {
        // A leave without an over after it means we're out of bounds.
        // A leave shortly followed by an over can be safely ignored, it just means we changed targets.
        if (!willLeave.current) return
        setDropIndex(undefined)
      }, 20)
    },
    onDrop() {
      // Not an internal dnd.
      if (dragIndex === undefined) return
      // The dnd is internal but somehow we never got to know about the dropIndex.
      if (dropIndex === undefined) return

      const toIndex = dragIndex < dropIndex ? dropIndex - 1 : dropIndex
      const newItems = move(items, dragIndex, toIndex)

      if (newItems !== items) onReorder(newItems)
    },
  }

  const autoScroll = useAutoScroll(containerRef, props)

  /* eslint-enable react-hooks/rules-of-hooks */

  return {
    dragState,
    preview,
  }
}

export type DragState = {
  previewRef: RefObject<DragPreviewRenderer>
  dragIndex: number | undefined
  dropIndex: number | undefined
  onDragStart: (index: number) => void
  onDragEnd: () => void
  onDragLeave: () => void
  onDrop: () => void
  onDragOver: (args: {
    clientX: number
    clientY: number
    rect: DOMRect
    itemIndex: number
  }) => void
  dragListId: string | null
}

//--------------------------------------
//  useDndListItem
//--------------------------------------

export interface UseDndListItemProps<ITEM> {
  itemState: State<ITEM>
  dragState: DragState | undefined
  renderDropIndicator: RenderDropIndicator | undefined
  listId: string
  itemCount: number
}

export function useDndListItem<ITEM>(props: UseDndListItemProps<ITEM>) {
  const { itemState, dragState, listId, itemCount } = props

  const renderDropIndicator = useStableCallback(props.renderDropIndicator)
  const lastMouseDownTarget = useRef<EventTarget>()

  // No drag & drop support? Nothing to do.
  if (!dragState) return { rowProps: {}, isDragging: false }

  const { previewRef, dropIndex } = dragState
  const isDragging = dragState.dragIndex === itemState.index

  const rowProps: LiHTMLAttributes<HTMLLIElement> = {
    onMouseDown(evt: MouseEvent) {
      lastMouseDownTarget.current = evt.target
    },

    onDragStart(evt: DragEvent) {
      evt.stopPropagation() // prevent nested drag & drop from triggering the others
      if (!isElement(lastMouseDownTarget.current)) return evt.preventDefault()

      // Forbid drag & drop initiated from interactive elements as it's likely a mistake.
      // Also it can collide with other gestures such as selecting a text range in a rich text by dragging the mouse.
      if (findFocusableAncestor(lastMouseDownTarget.current))
        return evt.preventDefault()

      dragState.onDragStart(itemState.index)

      evt.dataTransfer.dropEffect = "none"
      evt.dataTransfer.effectAllowed = "move"

      // Continue the drag & drop but with the default browser preview.
      if (typeof previewRef?.current !== "function") return

      previewRef.current(itemState.key, (node) => {
        const size = node.getBoundingClientRect()
        const rect = evt.currentTarget.getBoundingClientRect()

        let x = evt.clientX - rect.x
        let y = evt.clientY - rect.y
        if (x > size.width) x = Math.floor((x * size.width) / rect.width)
        if (y > size.height) y = Math.floor((y * size.height) / rect.height)

        const height = 2 * Math.round(size.height / 2)
        node.style.height = `${height}px`

        evt.dataTransfer.setDragImage(node, x, y)
      })
    },

    onDragEnd() {
      dragState.onDragEnd()
    },

    onDragOver(evt: DragEvent) {
      evt.preventDefault()

      const element = getMainElement(evt.currentTarget)
      if (!element) return

      dragState.onDragOver({
        rect: element.getBoundingClientRect(),
        clientX: evt.clientX,
        clientY: evt.clientY,
        itemIndex: itemState.index,
      })
    },

    onDragEnter(evt: DragEvent) {
      evt.preventDefault()
    },

    onDragLeave() {
      dragState.onDragLeave()
    },

    onDrop(evt: DragEvent) {
      evt.preventDefault()
      dragState.onDrop()
    },
  }

  const isDragWithinSameList = dragState.dragListId === listId

  const beforeDropIndicator =
    itemState.index === 0 ? (
      <ItemDropIndicator
        itemIndex={itemState.index}
        position="before"
        render={renderDropIndicator}
        isDropTarget={dropIndex === 0 && isDragWithinSameList && !isDragging}
        itemCount={itemCount}
      />
    ) : null

  const afterDropIndicator = (
    <ItemDropIndicator
      itemIndex={itemState.index}
      position="after"
      render={renderDropIndicator}
      isDropTarget={
        dropIndex === itemState.index + 1 &&
        dragState.dragIndex !== itemState.index + 1 &&
        isDragWithinSameList &&
        !isDragging
      }
      itemCount={itemCount}
    />
  )

  return {
    isDragging,
    rowProps,
    beforeDropIndicator,
    afterDropIndicator,
  }
}

export type RenderDropIndicator = (args: RenderDropIndicatorArgs) => ReactNode
export type RenderDragPreview<ITEM> = (
  args: RenderDragPreviewArgs<ITEM>,
) => ReactElement

export interface RenderDropIndicatorArgs {
  isDropTarget: boolean
  itemIndex: number
  position: "before" | "after"
  itemCount: number
}

export interface RenderDragPreviewArgs<ITEM> {
  item: ITEM
  index: number
}

interface ItemDropIndicatorProps {
  isDropTarget: boolean
  position: "before" | "after"
  itemIndex: number
  render?: RenderDropIndicator
  itemCount: number
}

const ItemDropIndicator = memo(function ItemDropIndicator(
  props: ItemDropIndicatorProps,
) {
  const { isDropTarget, position, itemIndex, render, itemCount } = props

  return (
    <div
      aria-hidden
      className={styles.dropIndicator}
      style={{
        // The rendered drop indicator often has an absolute position so it does not shift the list layout.
        // Giving it a high zIndex ensures it's being rendered on top of any pre-existing item border, etc.
        // The fact that it's an element that is only shown during dragging makes it ok.
        zIndex: isDropTarget ? 1 : undefined,
      }}
    >
      {render?.({ isDropTarget, position, itemIndex, itemCount })}
    </div>
  )
})
